/**
 * SKE-4810. Every Facebook Page and Instagram connect or reconnect starts here.
 *
 * `fbLogin` returns an access token unless the admin is on Facebook Login for
 * Business AND config carries the system-user Login Configuration, in which
 * case it returns an authorization code. A code is exchanged server-side for a
 * connection handle plus the Pages it reaches, so the browser never holds a
 * Meta token. Callers keep their existing code on `kind: "token"`.
 */
import {
  discardFacebookBusinessLoginConnection,
  exchangeFacebookBusinessLoginCode,
  type DiscoveredFacebookPage,
} from "./code-exchange";
import { askToConnectAnotherWay } from "./connect-another-way";
import {
  canFallBackToUserToken,
  type BusinessTokenType,
  createFacebookLoginCancelledError,
  fbLogin,
  isFacebookLoginCancelled,
  type FacebookAuthResponse,
  type FacebookTokenAuthResponse,
  type FbLoginOptions,
  type FBPageExtended,
  type FBPageWithInsta,
} from "./fb-sdk-utils";

export type { DiscoveredFacebookPage } from "./code-exchange";

export type FacebookAuthorisation =
  | { kind: "token"; auth: FacebookTokenAuthResponse }
  | {
      kind: "handle";
      connectionHandle: string;
      pages: DiscoveredFacebookPage[];
    };

export async function resolveFacebookAuthorisation(
  auth: FacebookAuthResponse
): Promise<FacebookAuthorisation> {
  if (auth.code !== undefined) {
    const connection = await exchangeFacebookBusinessLoginCode({
      code: auth.code,
    });
    return {
      kind: "handle",
      connectionHandle: connection.connectionHandle,
      pages: connection.pages,
    };
  }
  return { kind: "token", auth };
}

/*
 * SKE-5310. A System User login only reaches assets inside a business
 * portfolio the person selects, so a Page or Instagram account outside any
 * portfolio cannot be connected that way. When it finds nothing, the person is
 * asked whether to connect with the User-token configuration instead. Closing
 * Facebook's dialog looks exactly like finding nothing, so the second dialog
 * only ever opens from their choice, and they are asked at most once a login.
 */

/** `fbLogin`, offering the fallback when it comes back with no credential. */
export async function loginWithFallback(
  ...loginArgs: Parameters<typeof fbLogin>
): Promise<FacebookAuthResponse> {
  const [scopes, options = {}] = loginArgs;
  if (!canFallBackToUserToken(options)) {
    return fbLogin(...loginArgs);
  }
  let auth: FacebookAuthResponse | undefined;
  let cancelled: unknown;
  try {
    auth = await fbLogin(...loginArgs);
  } catch (error) {
    if (!isFacebookLoginCancelled(error)) throw error;
    cancelled = error;
  }
  if (auth?.code || auth?.accessToken) {
    return auth;
  }
  if (!(await askToConnectAnotherWay())) {
    throw cancelled ?? createFacebookLoginCancelledError();
  }
  return loginAsUserToken(scopes, options);
}

/** The "Connect another way" login. Coming back empty is a cancellation. */
async function loginAsUserToken(
  scopes: string[],
  options: FbLoginOptions
): Promise<FacebookAuthResponse> {
  const auth = await fbLogin(scopes, { ...options, businessTokenType: "user" });
  if (!auth.accessToken && !auth.code) {
    throw createFacebookLoginCancelledError();
  }
  return auth;
}

/**
 * Hands an unused connection back. The server deletes it and revokes only a
 * system-user credential. Not awaited: the next Facebook dialog must open
 * straight from the click.
 */
export function discardUnusedConnection(connectionHandle: string): void {
  void discardFacebookBusinessLoginConnection(connectionHandle).catch(
    (error: unknown) => {
      console.warn("Could not hand back an unused Facebook connection", error);
    }
  );
}

/** Before a list's "Connect another way": hand back the handle it came from. */
export function discardSessionHandle(session: FacebookConnectSession): void {
  if (isConnectionHandleSession(session)) {
    discardUnusedConnection(session.connectionHandle);
  }
}

/** `[scopes]`, plus the token type only when one was asked for. */
export function loginArgs(
  scopes: string[],
  businessTokenType?: BusinessTokenType
): Parameters<typeof fbLogin> {
  return businessTokenType ? [scopes, { businessTokenType }] : [scopes];
}

/**
 * `resolveFacebookAuthorisation`, offering the fallback when the code exchange
 * finds no Pages. Instagram accounts are discovered through their Pages, so no
 * Pages means no Instagram accounts either. The empty connection reaches
 * nothing, so it is handed back whatever the answer. Declining keeps the empty
 * result, unless the caller has nothing to show for it (`cancelOnDecline`, the
 * campaign pass): then the decline is a cancelled login.
 */
export async function resolveWithFallback(
  auth: FacebookAuthResponse,
  scopes: string[],
  options: FbLoginOptions = {},
  { cancelOnDecline = false }: { cancelOnDecline?: boolean } = {}
): Promise<FacebookAuthorisation> {
  const authorisation = await resolveFacebookAuthorisation(auth);
  if (
    authorisation.kind !== "handle" ||
    authorisation.pages.length > 0 ||
    !canFallBackToUserToken(options)
  ) {
    return authorisation;
  }
  const accepted = await askToConnectAnotherWay();
  discardUnusedConnection(authorisation.connectionHandle);
  if (!accepted) {
    if (!cancelOnDecline) return authorisation;
    throw createFacebookLoginCancelledError();
  }
  return resolveFacebookAuthorisation(await loginAsUserToken(scopes, options));
}

export async function authoriseFacebook(
  ...loginArgs: Parameters<typeof fbLogin>
): Promise<FacebookAuthorisation> {
  return resolveWithFallback(
    await loginWithFallback(...loginArgs),
    ...loginArgs
  );
}

/** What a picker carries instead of a Meta session once a handle exists. */
export interface ConnectionHandleSession {
  connectionHandle: string;
}

export type FacebookConnectSession =
  FacebookTokenAuthResponse | ConnectionHandleSession;

export function isConnectionHandleSession(
  session: FacebookConnectSession
): session is ConnectionHandleSession {
  return typeof session.connectionHandle === "string";
}

/** Body for `POST /accounts/facebook`, `POST /accounts/instagram` and `PUT /accounts/addIgToken/{id}`. */
export interface ConnectionHandlePageBody {
  connectionHandle: string;
  pageId: string;
}

/** Body for `POST /accounts/reconnectFacebook`. */
export interface ConnectionHandleReconnectBody {
  reconnectedAccount: string;
  connectionHandle: string;
}

export function toFacebookPickerPage(
  page: DiscoveredFacebookPage
): FBPageExtended {
  const name = page.pageName ?? "";
  return {
    id: page.pageId,
    name,
    name_with_location_descriptor: page.pageNameWithLocation ?? name,
    category: page.category,
    category_list: undefined,
    tasks: page.tasks,
  };
}

export function toInstagramPickerPage(
  page: DiscoveredFacebookPage
): FBPageWithInsta {
  const fbPage: FBPageWithInsta["page"] = {
    id: page.pageId,
    name: page.pageName ?? page.pageId,
  };
  const instagramId = page.instagram?.id ?? page.instagramBusinessAccountId;
  if (!instagramId) {
    return { page: fbPage };
  }
  fbPage.instagram_business_account = { id: instagramId };
  return {
    page: fbPage,
    ig: {
      id: instagramId,
      ig_id: page.instagram?.igId,
      username: page.instagram?.username ?? page.instagramUsername,
      name: page.instagram?.name,
      followers_count: page.instagram?.followersCount,
      profile_picture_url: page.instagram?.profilePictureUrl,
      website: page.instagram?.website,
    },
  };
}
